/**
 * External dependencies
 */
import { DateTime } from 'luxon';
import { Archive, Star } from 'lucide-react';

/**
 * Internal dependencies
 */
import { Badge } from '@Components/Badge';
import {
  EventStatus,
  EventVisibility,
  type TicketedEvent,
} from '@Types/ticketedEvents';

export interface StatusBadgeConfig {
  label: string;
  variant?: 'default' | 'secondary' | 'destructive' | 'outline';
  className?: string;
}

export interface VisibilityBadgeConfig {
  label: string;
  variant?: 'default' | 'secondary' | 'destructive' | 'outline';
  className?: string;
}

/**
 * Get the status badge configuration for an event
 */
export function getEventStatusBadge(
  event: TicketedEvent,
): StatusBadgeConfig | null {
  if (event.status === EventStatus.DRAFT) {
    return { label: 'Draft', variant: 'secondary', className: '' };
  }

  if (event.status === EventStatus.PUBLISHED) {
    if (event.startDateTime) {
      const eventDate = DateTime.fromISO(event.startDateTime);
      const today = DateTime.now().startOf('day');
      const eventDay = eventDate.startOf('day');

      if (eventDay < today) {
        return {
          label: 'Past Event',
          variant: 'destructive',
        };
      }
    }
    return { label: 'Published', variant: 'default', className: '' };
  }

  return null;
}

/**
 * Get the visibility badge configuration for an event
 */
export function getEventVisibilityBadge(
  event: TicketedEvent,
): VisibilityBadgeConfig | null {
  if (event.visibility === EventVisibility.MEMBERS_ONLY) {
    return {
      label: 'Members Only',
      className: 'bg-[#caab6a] text-black',
    };
  }

  if (event.visibility === EventVisibility.PUBLIC) {
    return {
      label: 'Public Event',
      variant: 'secondary',
      className: '',
    };
  }

  return null;
}

/**
 * Status Badge Component
 */
export function EventStatusBadge({ event }: { event: TicketedEvent }) {
  const statusBadge = getEventStatusBadge(event);

  if (!statusBadge) return null;

  return (
    <Badge variant={statusBadge.variant} className={statusBadge.className}>
      {statusBadge.label}
    </Badge>
  );
}

/**
 * Visibility Badge Component
 */
export function EventVisibilityBadge({ event }: { event: TicketedEvent }) {
  const visibilityBadge = getEventVisibilityBadge(event);

  if (!visibilityBadge) return null;

  return (
    <Badge
      variant={visibilityBadge.variant}
      className={visibilityBadge.className}
    >
      {event.visibility === EventVisibility.MEMBERS_ONLY && (
        <Star className="h-3 w-3" />
      )}
      {visibilityBadge.label}
    </Badge>
  );
}

/**
 * Archived Badge Component
 */
export function EventArchivedBadge({ event }: { event: { archivedAt?: string | null } }) {
  if (!event.archivedAt) return null;

  return (
    <Badge variant="outline" className="text-destructive border-destructive">
      <Archive className="h-3 w-3" />
      Archived
    </Badge>
  );
}

/**
 * Combined Badges Component - displays both status and visibility badges
 */
export function EventBadges({ event }: { event: TicketedEvent }) {
  const statusBadge = getEventStatusBadge(event);
  const visibilityBadge = getEventVisibilityBadge(event);

  return (
    <div className="flex flex-wrap gap-1">
      {statusBadge && (
        <Badge variant={statusBadge.variant} className={statusBadge.className}>
          {statusBadge.label}
        </Badge>
      )}
      {visibilityBadge && (
        <Badge
          variant={visibilityBadge.variant}
          className={visibilityBadge.className}
        >
          {event.visibility === EventVisibility.MEMBERS_ONLY && (
            <Star className="h-3 w-3" />
          )}
          {visibilityBadge.label}
        </Badge>
      )}
    </div>
  );
}
